import { StyleSheet, View, Image, TextInput, TouchableWithoutFeedback, ActivityIndicator, Text, Pressable } from 'react-native';
import { useState, useEffect, useRef } from 'react';
import { doc, collection, setDoc, addDoc, Timestamp, getDoc, updateDoc, query, where, getDocs } from "firebase/firestore"; 
import { GooglePlacesAutocomplete } from 'react-native-google-places-autocomplete';
import { getLocation } from '../../services/utils/General'
import { getUserDataByUserId } from '../../services/users/Users';
import { db } from '../../firebaseConfig';
const geofire = require('geofire-common');
import Button from '../shared/Button';

export default function ManageBusiness({ route, navigation }) {

  const [location, setLocation] = useState(null);
  const [isBusinessRepresentative, setIsBusinessRepresentative] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const refBusiness = useRef(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [googlePlacesDetails, setGooglePlacesDetails] = useState(null);
  const [googlePlaceId, setGooglePlaceId] = useState('');
  const [animating, setAnimating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [businessId, setBusinessId] = useState(null);
  const [showPendingApprovalMessage, setShowPendingApprovalMessage] = useState(false);

  useEffect(() => {
    (async () => {

      let savedUserId = window.localStorage.getItem('userId');
      if (!savedUserId) {
        navigation.navigate('CreateAccountScreen');
        return;
      }
      await loadUserInformation(savedUserId);

      const editBusinessId = route?.params?.businessId;
      if (editBusinessId) {
        setBusinessId(editBusinessId);
        setIsUpdating(true);
        await loadBusinessInformation(editBusinessId);
        navigation.setOptions({ title: 'Edit Your Business' });
      } else {
        navigation.setOptions({ title: 'Add Your Business' });
      }

      let hasRequestedPermission = window.localStorage.getItem('hasRequestLocationPermission');
      if (hasRequestedPermission) {
        var locationResult = await getLocation();
        if (locationResult) {
          // console.log("locationResult from getLocation!:", locationResult);
          setLocation(locationResult);
        }
      }

    })();
  }, [route])

  const loadUserInformation = async (userId) => {
    const userData = await getUserDataByUserId(userId);
    if (userData.isBusinessRep) {
      setIsBusinessRepresentative(userData.isBusinessRep);
    }
    if (userData.businessName) {
      setBusinessName(userData.businessName);
    }
    navigation.setOptions({ title: 'Your Profile' });
  }

  const loadBusinessInformation = async (id) => {
    const businessRef = doc(db, "Businesses", id);
    const businessSnap = await getDoc(businessRef);
    if (businessSnap.exists()) {
      const data = businessSnap.data();
      setBusinessName(data.bar ? data.bar : '');
      // pre-fill the autocomplete field, since it's an uncontrolled input
      refBusiness.current?.setAddressText(data.bar ? data.bar : '');
    }
  }

  const handleSubmit = async (e) => {
    setIsSubmitDisabled(true);
    // show activity indicator
    setAnimating(true);

    if (googlePlacesDetails) {
      // console.log('using googlePlacesDetails', googlePlacesDetails);
      const lat = googlePlacesDetails.geometry.location.lat;
      const lng = googlePlacesDetails.geometry.location.lng;
      const hash = geofire.geohashForLocation([lat, lng]);
      const barName = googlePlacesDetails.name;

      if (isUpdating && businessId) {
        const businessRef = doc(db, "Businesses", businessId);
        await updateDoc(businessRef, {
          bar: barName,
          placeId: googlePlaceId,
          latitude: lat,
          longitude: lng,
          geohash: hash,
          geohash2: hash.substring(0, 2),
          geohash3: hash.substring(0, 3),
          geohash4: hash.substring(0, 4),
          geohash5: hash.substring(0, 5),
          geohash6: hash.substring(0, 6),
        });
      } else {
        // Add a new document with a generated id.
        const docRef = await addDoc(collection(db, 'Businesses'), {
          userId: [window.localStorage.getItem('userId')],
          created: Timestamp.now(),
          bar: barName,
          placeId: googlePlaceId,
          latitude: lat,
          longitude: lng,
          geohash: hash,
          geohash2: hash.substring(0, 2),
          geohash3: hash.substring(0, 3),
          geohash4: hash.substring(0, 4),
          geohash5: hash.substring(0, 5),
          geohash6: hash.substring(0, 6),
          status: ['pendingapproval'],
        });
        setBusinessId(docRef.id);
        setIsUpdating(true);
      }
    }

    setIsSubmitDisabled(false);
    setAnimating(false);
    setShowPendingApprovalMessage(true);
    setIsDirty(false);
  }


  return (
    <View style={styles.container}>
      <View style={styles.formContainer}>
        <Text style={styles.fieldValue}>
          {'Type a business name below, then select your business from the list of results.'}
        </Text>
        <View style={styles.businessInputContainer}>
          <GooglePlacesAutocomplete
            placeholder="Enter your business"
            ref={refBusiness}
            fetchDetails={true}
            textInputProps={{
              placeholderTextColor: '#999',
              onChangeText: (text) => {
                if (!text) {
                  setIsDirty(false);
                  setGooglePlacesDetails(null);
                  setGooglePlaceId('');
                }
              },
            }}
            minLength={2}
            onPress={(data, details = null) => {
              setBusinessName(data.description);
              // console.log("details from Google Places:", details);
              setIsDirty(true);
              setGooglePlacesDetails(details);
              setGooglePlaceId(data.place_id || (details && details.place_id) || '');
            }}
            query={{
              language: 'en',
              lat: location ? location.coords.latitude : null,
              lng: location ? location.coords.longitude : null,
            }}
            requestUrl={{
              useOnPlatform: 'web',
              url: 'https://us-central1-atabar-d7693.cloudfunctions.net/placesApiSearch',
            }}
            styles={{
              textInput: styles.input,
            }}
          />

        </View>
        {showPendingApprovalMessage &&
          <Text style={styles.pendingApprovalMessage}>
            {isUpdating
              ? 'Saved. Your business information has been updated.'
              : 'Saved. Once your association with the business is approved, you will be able to create Bar Posts.'}
          </Text>
        }
        <View style={styles.buttonSection}>
          <ActivityIndicator style={{ position: 'absolute', bottom: '50%', right: '50%',}} size="large" color="#ffd33d" animating={animating} hidesWhenStopped={true} />
          {isDirty ?
            <Button theme="basic" label="Save" onPress={handleSubmit} isDisabled={isSubmitDisabled} />
            :
            <Button theme="basic" label="Back" onPress={() => navigation.navigate('CreateAccountScreen')} />
          }
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F2F2F2',
    alignItems: 'center',
    paddingTop: 18,
    paddingHorizontal: 12,
  },
  fieldLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 5,
  },
  fieldValue: {
    fontSize: 16,
    color: '#555',
    paddingVertical: 5,
  },
  pendingApprovalMessage: {
    fontSize: 16,
    fontWeight: '600',
    color: '#8a6d00',
    backgroundColor: '#fff4cc',
    borderWidth: 1,
    borderColor: '#ffd33d',
    borderRadius: 8,
    padding: 10,
    marginTop: 8,
    width: '100%',
  },
  formContainer: {
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
  },
  input: {
    height: 44,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 16,
    color: '#333',
    width: '100%',
    marginBottom: 10,
  },
  businessInputContainer: {
    width: '100%',
    marginTop: 8,
    zIndex: 1,
  },
  buttonSection: {
    width: '100%',
    marginTop: 10,
  },
});